/**
 * spiffApproval.ts — the Monday SPIFF approval (Oct 27, 2026; migration 20261027j_spiff_approval.sql).
 *
 * Greg: "lets adjust the spiff approval to alex poma on monday morning". The weekly run (Monday 12:05 PM ET, after
 * the 5-Star Review cutoff) records last week's SPIFFs waiting; Alex approves them on the ASM board (Chad or Greg
 * when he hasn't); approving makes one Books bill per person; Shannon pays them in Books on Tuesday. A SPIFF a
 * manager gives by hand waits for the same approval.
 *
 * Who approves mirrors books_payout_approve_tier('spiff'): the ROSTER role (useAuth().role), not the super-user
 * list, so Jennifer and Jeremy, super users with other roles, do not approve.
 */

export const SPIFF_APPROVER_ROLES = ["asm", "sales_director", "admin"] as const;

/** Whether this roster role approves SPIFFs (the database checks it again). */
export function canApproveSpiffs(role: string | null | undefined): boolean {
  return !!role && (SPIFF_APPROVER_ROLES as readonly string[]).includes(role);
}

export interface ApprovalSpiff {
  id: string;
  user_id: string;
  recipient_name: string;
  name: string;
  amount_cents: number;
  claimed_at: string;
  source: string;
  job_address: string | null;
  approved_at: string | null;
  /** Who approved it (spiffs_for_approval); absent on rows read elsewhere. */
  approved_by_name?: string | null;
  paid_at: string | null;
  books_bill_id: string | null;
}

/** A SPIFF waits for approval while it is not approved, not paid and on no Books bill. */
export function isWaiting(r: Pick<ApprovalSpiff, "approved_at" | "paid_at" | "books_bill_id">): boolean {
  return !r.approved_at && !r.paid_at && !r.books_bill_id;
}

/** ET Monday (YYYY-MM-DD) of the week a SPIFF was claimed in. */
export function spiffWeekOf(claimedAt: string): string {
  const et = new Date(claimedAt).toLocaleDateString("en-CA", { timeZone: "America/New_York" });
  const d = new Date(et + "T12:00:00Z");
  const dow = d.getUTCDay();
  d.setUTCDate(d.getUTCDate() + (dow === 0 ? -6 : 1 - dow));
  return d.toISOString().slice(0, 10);
}

export interface PersonGroup {
  user_id: string;
  name: string;
  rows: ApprovalSpiff[];
  cents: number;
}

export interface WeekGroup {
  monday: string;
  people: PersonGroup[];
  cents: number;
  count: number;
}

/**
 * The waiting SPIFFs by week (oldest first: the week that is due) and, inside a week, by person (largest first;
 * an ASM override line sits with the person who gets it, i.e. Alex).
 */
export function groupWaiting(rows: ApprovalSpiff[]): WeekGroup[] {
  const weeks = new Map<string, Map<string, PersonGroup>>();
  for (const r of rows) {
    if (!isWaiting(r)) continue;
    const wk = spiffWeekOf(r.claimed_at);
    const people = weeks.get(wk) ?? new Map<string, PersonGroup>();
    const p = people.get(r.user_id) ?? { user_id: r.user_id, name: r.recipient_name, rows: [], cents: 0 };
    p.rows.push(r);
    p.cents += r.amount_cents;
    people.set(r.user_id, p);
    weeks.set(wk, people);
  }
  return [...weeks.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([monday, people]) => {
      const list = [...people.values()]
        .map((p) => ({ ...p, rows: [...p.rows].sort((a, b) => a.claimed_at.localeCompare(b.claimed_at) || a.name.localeCompare(b.name)) }))
        .sort((a, b) => b.cents - a.cents || a.name.localeCompare(b.name));
      return {
        monday,
        people: list,
        cents: list.reduce((s, p) => s + p.cents, 0),
        count: list.reduce((s, p) => s + p.rows.length, 0),
      };
    });
}

/** "Week of Oct 26 – Nov 1" for an ET Monday. */
export function weekLabel(monday: string): string {
  const mon = new Date(monday + "T12:00:00Z");
  const sun = new Date(mon);
  sun.setUTCDate(sun.getUTCDate() + 6);
  const f = (x: Date) => x.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" });
  return `Week of ${f(mon)} – ${f(sun)}`;
}
